import type { SupabaseClient } from '@supabase/supabase-js';

export type VisioMessageSource = 'typed' | 'voice' | 'file';

export interface VisioMessage {
  id: string;
  roomId: string;
  senderId: string;
  body: string;
  /**
   * 'voice' = phrase reconnue en live par la Web Speech API (jamais d'audio stocké) ;
   * 'file' = fichier partagé (migration 0262) : `body` est alors le NOM du fichier.
   */
  source: VisioMessageSource;
  /** Fichier partagé (`source='file'`). `null` sur un message-fichier = fichier supprimé depuis. */
  documentId: string | null;
  createdAt: string;
  /** Non null = message MASQUÉ (suppression douce, réversible — migration 0246) : jamais affiché dans le chat, exclu du résumé. */
  hiddenAt: string | null;
}

/** Nom + avatar d'un des deux participants d'une salle (RPC `get_visio_room_participants`). */
export interface VisioParticipantInfo {
  userId: string;
  role: 'tutor' | 'student';
  displayName: string;
  avatarUrl: string | null;
}

export interface MessagingService {
  history(roomId: string): Promise<VisioMessage[]>;
  send(roomId: string, body: string, source?: 'typed' | 'voice'): Promise<void>;
  /** Publie dans le chat un fichier déjà déposé dans la salle (RPC `send_visio_room_file`, migration 0262). */
  sendFile(roomId: string, documentId: string): Promise<void>;
  /** Masque (`true`) ou rétablit (`false`) un message — l'auteur, ou le tuteur de la salle. */
  setHidden(messageId: string, hidden: boolean): Promise<VisioMessage>;
  participants(roomId: string): Promise<VisioParticipantInfo[]>;
  /** Nouveaux messages ET messages modifiés (masqués/rétablis) — à fusionner par `id`. */
  onMessage(roomId: string, cb: (message: VisioMessage) => void): () => void;
}

interface RawMessageRow {
  id: string;
  room_id: string;
  sender_id: string;
  body: string;
  source: VisioMessageSource;
  created_at: string;
  hidden_at?: string | null;
  document_id?: string | null;
}

interface RawParticipantRow {
  user_id: string;
  role: 'tutor' | 'student';
  display_name: string | null;
  avatar_url: string | null;
}

export function fromRow(row: RawMessageRow): VisioMessage {
  return {
    id: row.id,
    roomId: row.room_id,
    senderId: row.sender_id,
    body: row.body,
    source: row.source,
    documentId: row.document_id ?? null,
    createdAt: row.created_at,
    hiddenAt: row.hidden_at ?? null,
  };
}

/**
 * Postgres = source de vérité, livraison live via `postgres_changes` — pas de
 * broadcast séparé (évite le split-brain DB/broadcast). Autorisé
 * indépendamment de la salle vidéo : `postgres_changes` est gated par la RLS
 * de la table source (`visio_room_messages: participants_select`, migration
 * 0234), PAS par la "Realtime Authorization" des canaux privés (celle-ci ne
 * couvre que Broadcast/Presence, cf. migration 0236) — donc pas besoin de
 * `private: true` ni du même topic que la signalisation vidéo.
 *
 * `db` est INJECTÉ (pas de singleton importé) : `visio_room_messages` n'est
 * pas déclarée dans le `Database` type partagé (convention du repo pour les
 * tables postérieures aux types stale, cf. apps/app/src/lib/tutor/booking.ts)
 * — chaque app appelante passe son propre client, castée en `SupabaseClient`
 * générique pour ce seul accès table ; `send_visio_room_message` (RPC), lui,
 * EST typé (packages/db/src/types.ts).
 */
export class SupabaseMessagingService implements MessagingService {
  constructor(private readonly db: SupabaseClient) {}

  async history(roomId: string): Promise<VisioMessage[]> {
    const { data, error } = await this.db
      .from('visio_room_messages')
      .select('id, room_id, sender_id, body, source, created_at, hidden_at, document_id')
      .eq('room_id', roomId)
      .order('created_at', { ascending: true });
    if (error) throw error;
    return ((data ?? []) as RawMessageRow[]).map(fromRow);
  }

  async send(roomId: string, body: string, source: 'typed' | 'voice' = 'typed'): Promise<void> {
    const { error } = await this.db.rpc('send_visio_room_message', {
      p_room_id: roomId,
      p_body: body,
      p_source: source,
    });
    if (error) throw error;
  }

  async sendFile(roomId: string, documentId: string): Promise<void> {
    const { error } = await this.db.rpc('send_visio_room_file', {
      p_room_id: roomId,
      p_document_id: documentId,
    });
    if (error) throw error;
  }

  async setHidden(messageId: string, hidden: boolean): Promise<VisioMessage> {
    const { data, error } = await this.db.rpc('set_visio_room_message_hidden', {
      p_message_id: messageId,
      p_hidden: hidden,
    });
    if (error) throw error;
    // Un RPC qui renvoie un type composite répond par un objet (ou un tableau d'1 ligne selon la version de PostgREST).
    const row = (Array.isArray(data) ? data[0] : data) as RawMessageRow;
    return fromRow(row);
  }

  async participants(roomId: string): Promise<VisioParticipantInfo[]> {
    const { data, error } = await this.db.rpc('get_visio_room_participants', {
      p_room_id: roomId,
    });
    if (error) throw error;
    return ((data ?? []) as RawParticipantRow[]).map((row) => ({
      userId: row.user_id,
      role: row.role,
      displayName: row.display_name ?? '',
      avatarUrl: row.avatar_url,
    }));
  }

  onMessage(roomId: string, cb: (message: VisioMessage) => void): () => void {
    const channel = this.db
      .channel(`visio-room-chat:${roomId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'visio_room_messages',
          filter: `room_id=eq.${roomId}`,
        },
        (payload: { new: RawMessageRow }) => cb(fromRow(payload.new)),
      )
      // UPDATE (message masqué / rétabli par l'un ou l'autre participant) : SANS
      // filtre serveur — avec l'identité de réplica par défaut, un filtre sur une
      // colonne non-PK n'est pas fiable pour les UPDATE. La RLS limite déjà aux
      // salles du participant ; on filtre la salle côté client.
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'visio_room_messages' },
        (payload: { new: RawMessageRow }) => {
          if (payload.new.room_id === roomId) cb(fromRow(payload.new));
        },
      )
      .subscribe();

    return () => {
      void channel.unsubscribe();
    };
  }
}
